import { FORM_IDS_SEPARATOR, PartType } from './constants';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import memoize from 'memoize-one';
import some from 'lodash/some';
import reduce from 'lodash/reduce';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';

export const getFieldOption = (field, value, isPilot = false) =>
  (
    (typeof field.options === 'function'
      ? field.options(isPilot)
      : field.options) || []
  ).find((option) => option.value === value) || {};

/**
 * This function is used to replace all the characters within a text with '*'.
 * This is used for Assign/Manage form to align left options on the same level with right options.
 * @param {string} text - The text to replace
 * @returns The same text with all characters replaced with '*'.
 * */
export const getAlignText = (text) => text.replace(/[^\s]/g, '*');

/**
 * Builds a form ID for specific objects based on multiple IDS.
 * E.g. STUDENT_ID__SECTION_ID__ASSIGNMENT_ID or SECTION_ID__ASSIGNMENT_ID
 * @param {array} args - A list of arguments to build the form ID field from.
 * @returns {string} String of form ARG1__ARG2__ARG3.
 * */
export const getFormIdField = (...args) =>
  args.filter(Boolean).join(FORM_IDS_SEPARATOR);

/**
 * Classes stats based on student selections
 * @param {func} dispatch
 * @param {object} stats The existing stats
 * @param {string} step add | remove
 * @param {array} students
 * @param {array} disabledStudents
 * @param {array} classList List of classes with students list
 * */
export const calculateClassesStats = ({
  stats = {},
  step = 'add',
  students = [],
  disabledStudents = [],
  classList = [],
}) => {
  const calcStep = (value) => (step === 'add' ? value + 1 : value - 1);

  const newStats = {
    ...stats,
  };

  classList.forEach(({ id: sectionId, students }) => {
    const sectionStats = newStats[sectionId] || null;
    const studentsLength = (students || []).length;
    if (sectionStats) {
      newStats[sectionId].students.all = studentsLength;
    } else {
      newStats[sectionId] = {
        students: {
          all: studentsLength,
          selected: 0,
          disabled: 0,
        },
      };
    }
  });

  if (isEmpty(newStats)) {
    return;
  }

  students.forEach(({ sectionId: secId }) => {
    const sectionId = secId.toString();
    newStats[sectionId].students.selected = calcStep(
      newStats[sectionId].students.selected,
    );
  });

  disabledStudents.forEach(({ sectionId: secId }) => {
    const sectionId = secId.toString();
    newStats[sectionId].students.disabled += 1;
  });

  //For safety
  forEach(newStats, ({ students }, sectionId) => {
    const { selected, disabled, all } = students;
    if (selected < 0) {
      newStats[sectionId].students.selected = 0;
    } else if (all && selected > all - disabled) {
      newStats[sectionId].students.selected = all - disabled;
    }
  });

  forEach(newStats, (s, key) => {
    newStats[key].created = true;
  });

  return newStats;
};

/**
 * @param {array} sections - The array of all classes to search for.
 * Each object shall have the `students` property.
 * @param {number|string} sectionId - The ID of the section to search for.
 * @returns {array} - The list of section's students.
 * */
export const getSectionStudents = (sections, sectionId) =>
  (((sections || []).find((s) => s.id === sectionId) || {}).students || []).map(
    (student) => ({
      ...student,
      sectionId,
    }),
  );

/**
 * Get all the students from all the sections the teacher has
 * */
export const getStudentsBySections = memoize(
  (subjectClasses = [], searchedSections = null) => {
    const withoutSections = searchedSections === null;
    const defaultAllAssigneeIds = withoutSections
      ? []
      : searchedSections.map((a) => a.sectionId);
    return subjectClasses.reduce((acc, section) => {
      const { id: sectionId } = section;
      const assigneeClass = withoutSections
        ? {}
        : searchedSections.find(
            (assignee) => assignee.sectionId === sectionId,
          ) || {};
      const assignmentId = assigneeClass.assignmentId || undefined;
      if (withoutSections || defaultAllAssigneeIds.includes(sectionId)) {
        return acc.concat(
          section.students.map((student) => ({
            ...student,
            id: getFormIdField(student.id, sectionId, assignmentId),
            studentId: student.id,
            sectionId,
            assignmentId,
          })),
        );
      }

      return acc;
    }, []);
  },
);

const isFullClassSelections = ({ selected, disabled, all }) =>
  selected > 0 && all === selected + disabled;
const isISA = ({ selected, disabled, all }) =>
  disabled && isFullClassSelections({ selected, disabled, all });

/**
 * Check within classes stats if it's the only class with selections
 * @param {string} sectionId
 * @param {object} classesStats
 * @returns {boolean} If no other classes has selections, current class has disabled and selected all available selections
 * */
export const isOnlyISAWithSelections = (sectionId, classesStats) => {
  let foundOtherSelections = false;
  forEach(classesStats, ({ students }, currentSectionId) => {
    const { selected } = students;
    if (
      currentSectionId !== sectionId &&
      !foundOtherSelections &&
      selected > 0
    ) {
      foundOtherSelections = true;
    }
  });

  const {
    students: { all, selected, disabled },
  } = classesStats[sectionId] || { students: {} };
  return !foundOtherSelections && isISA({ selected, disabled, all });
};

/**
 * Checks if there is a ISA class with selections (selected and disabled)
 * and if all others are full class selections
 * @param {object} classesStats
 * @returns {boolean}
 * */
export const isISAWithFullClassesSelections = (classesStats) => {
  let isaFound = false;
  let fullClassSelections = true;
  some(classesStats, ({ students: { disabled, selected, all } }) => {
    if (isISA({ selected, disabled, all })) {
      isaFound = true;
    }

    if (selected > 0 && !isFullClassSelections({ selected, disabled, all })) {
      fullClassSelections = false;

      return true;
    }
  });

  return isaFound && fullClassSelections;
};

export const getSelectedLength = (classesStats) =>
  reduce(
    classesStats,
    (total, { students: { selected } }) => total + selected,
    0,
  );

/**
 * Checks if all selections are full class selections without ISA selections
 * @param {object} classesStats
 * @returns {boolean}
 * */
export const isFullClassesSelections = (classesStats) => {
  const isFullClassSelections = ({ selected, disabled, all }) =>
    selected > 0 && all === selected + disabled;
  const isISA = ({ selected, disabled, all }) =>
    disabled && isFullClassSelections({ selected, disabled, all });

  let fullClassesSelections = false;
  some(classesStats, ({ students: { disabled, selected, all } }) => {
    if (
      isISA({ selected, disabled, all }) ||
      (selected > 0 && !isFullClassSelections({ selected, disabled, all }))
    ) {
      fullClassesSelections = false;
      return true;
    } else if (isFullClassSelections({ selected, disabled, all })) {
      fullClassesSelections = true;
      return false;
    }
  });

  return fullClassesSelections;
};

/**
 * Gets the assessmentGroup administration window dates according to assessment semester.
 *
 * @param {{window, semester}} assessmentGroupInfo
 * @return { minStartDate, maxStartDate } Minimum and maximum start dates.
 */
export const getFinalExamAdministrationWindowDates = (
  assessmentGroupInfo,
  timezone,
) => {
  if (!assessmentGroupInfo) {
    return { minStartDate: null, maxStartDate: null };
  }
  const minStartDate = dayjs(
    assessmentGroupInfo.semester === 'winter'
      ? assessmentGroupInfo.window.winterWindowStartDate
      : assessmentGroupInfo.window.springWindowStartDate,
  ).tz(timezone);
  const maxStartDate = dayjs(
    assessmentGroupInfo.semester === 'winter'
      ? assessmentGroupInfo.window.winterWindowEndDate
      : assessmentGroupInfo.window.springWindowEndDate,
  ).tz(timezone);
  return { minStartDate, maxStartDate };
};

/**
 * We're using a combination of SECTION_ID__ASSIGNMENT_ID for our classes within the assign/manage form.
 * This function is used to set specific structure of a list of sections.
 * @param {array} sections - The list of sections
 * E.g. [{
 *   id: <SECTION_ID>,
 *   name: <SECTION_NAME>,
 *   assignmentId: <ASSIGNMENT_ID>
 * }]
 * @returns {array} - The list update with SECTION_ID__ASSIGNMENT_ID combination as ID field of each object.
 * E.g. [{
 *   id: <SECTION_ID>__<ASSIGNMENT_ID>,
 *   name: <SECTION_NAME>,
 *   sectionId: <SECTION_ID>,
 *   assignmentId: <ASSIGNMENT_ID>
 * }]
 * */
export const updateSectionsWithFormStructure = (sections) =>
  sections.map((section) => ({
    ...section,
    id: getFormIdField(section.id, section.assignmentId),
    sectionId: section.id.toString(),
    assignmentId: section.assignmentId ? section.assignmentId : undefined,
  }));

/**
 * Gets the dates taking in consideration when its a final exam with a grouped assignment.
 * If the current assignment is part 2 (after the related) then the startsAt and startsAtPart2
 * are reversed.
 *
 * @param {{related_assignments}} assignment
 * @param {{startsAt,startsAtPart2}} dates used on return.
 * @param {boolean} isFinalExam
 * @param {[{partType, assessmentId, sortOrder}]} assessmentGroupMembers
 * @return { startsAt, startsAtPart2 }
 */
export const getAssignmentDates = (
  assignment,
  dates,
  isFinalExam,
  assessmentGroupMembers = [],
) => {
  const { related_assignments: relatedAssignments } = assignment;
  const { startsAt, startsAtPart2 } = dates;
  const isTwoPartsFinalExam = isFinalExam && !!relatedAssignments.length;
  const orderedMembers = isTwoPartsFinalExam
    ? assessmentGroupMembers
        .filter((m) => m.partType === PartType.PARTIAL.value)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((m) => m.assessmentId)
    : [];
  const isCurrentAssignmentBeforeRelated =
    !isFinalExam ||
    !isTwoPartsFinalExam ||
    orderedMembers.indexOf(assignment.item.id) <=
      orderedMembers.indexOf(relatedAssignments[0].item_id);
  return isCurrentAssignmentBeforeRelated
    ? { startsAt, startsAtPart2 }
    : { startsAtPart2: startsAt, startsAt: startsAtPart2 };
};

export const getNowMinTimezoneTime = (
  isFinalExam,
  timezone,
  assessmentGroupInfo,
) => {
  if (!!timezone) {
    let nowMinTimezone = dayjs().tz(timezone);

    if (isFinalExam) {
      const { minStartDate } = getFinalExamAdministrationWindowDates(
        assessmentGroupInfo,
        timezone,
      );
      const minStartDateMoment = dayjs(minStartDate).utcOffset(
        nowMinTimezone.utcOffset(),
        true,
      );
      if (minStartDateMoment.isAfter(nowMinTimezone, 'second')) {
        nowMinTimezone = minStartDateMoment;
      }
    }

    return nowMinTimezone.startOf('minute');
  }

  return null;
};

/**
 * Checks for differences between the two lists of students.
 * @param {array} formStudents - The list of students within the form (selected students).
 * @param {array} sectionInitialStudents - The list of students initially selected for this section.
 * @param {number|string} sectionId - The ID of the section.
 * @param {number|string} assignmentId - The ID of the assignment.
 * @returns {boolean} if the `sectionId`'s section students within `formStudents`
 * contains other students than the `sectionInitialStudents`
 * */
export const checkUpdatedStudents = (
  formStudents,
  sectionInitialStudents,
  sectionId,
  assignmentId,
) => {
  const sectionIdInt = Number.parseInt(sectionId, 10);
  const assignmentIdInt = Number.parseInt(assignmentId, 10);

  const formSectionStudentIds = formStudents
    .filter(
      (student) =>
        Number.parseInt(student.sectionId, 10) === sectionIdInt &&
        Number.parseInt(student.assignmentId, 10) === assignmentIdInt,
    )
    .map((student) => student.id)
    .sort();
  const sectionInitialStudentIds = sectionInitialStudents
    .map((student) => student.id)
    .sort();

  return !isEqual(formSectionStudentIds, sectionInitialStudentIds);
};

/**
 * Checks if the timer or isSoftTimer values have changed and adds isHardTimer if necessary.
 * @param {Object} currentValues - The current form values
 * @param {Object} initialValues - The initial form values
 * @param {Object} submitProps - The object to potentially add isHardTimer to
 * @returns {Object} - The updated submitProps object
 */
export const addIsHardTimerIfChanged = (
  currentValues,
  initialValues,
  submitProps,
  allowHardTimer,
) => {
  if (!allowHardTimer) return submitProps;

  if (
    currentValues.timer !== initialValues.timer ||
    currentValues.isSoftTimer !== initialValues.isSoftTimer
  ) {
    submitProps.isHardTimer = !!(
      currentValues.timer && !currentValues.isSoftTimer
    );

    // If there's no timer, set isHardTimer=true
    if (!currentValues.timer) {
      submitProps.isHardTimer = true;
    }
  }
  return submitProps;
};
